import React, { useCallback } from 'react';
import { Provider } from 'react-redux';
import { ErrorBoundary } from '@sde/widgets-core/src/common/ErrorBoundary';
import { createRootReducer } from '@sde/widgets-core/src/redux/helpers/createRootReducer';
import { configureStore } from '@sde/widgets-core/src/redux/store';
import { event } from '@uhg-abyss/web/tools/event';
import { PropsContext } from '@uhg-abyss/web/hooks/usePropsContext/PropsContext';

const widgetStyle = { height: '100%', width: '100%' };

const STORE_LIB = {};
const LOADED_WIDGETS = {};

const generateUniqueId = (widgetId) => {
  return `${widgetId}_${Math.random().toString(36).substring(7)}`;
};

const getWidgetProps = (component, props) => {
  const defaultProps = component.defaultProps || {};

  const data = { ...defaultProps.data, ...props.data };
  const events = { ...defaultProps.events, ...props.events };
  const styles = { ...defaultProps.styles, ...props.styles };

  const eventsFn = (key, ...args) => {
    if (typeof events[key] === 'function') {
      return events[key](...args);
    }

    return () => null;
  };

  return {
    data,
    events: Object.assign(eventsFn, events),
    styles,
  };
};

export const exporter = (component, options) => {
  const { id, reducers } = options || {};
  const widgetId = id || component.displayName;
  const uniqueWidgetId = generateUniqueId(widgetId);

  return function AbyssWidget(props) {
    // eslint-disable-next-line
    const instanceId = props.id || '__abyss_store__';
    let store = STORE_LIB[instanceId];

    if (store && !LOADED_WIDGETS[uniqueWidgetId]) {
      const newReducer = createRootReducer({
        ...reducers,
        ...store.loadedReducers,
        storeId: () => instanceId,
      });

      store.loadedReducers = {
        ...reducers,
        ...store.loadedReducers,
      };

      store.replaceReducer(newReducer);
    }

    if (!store) {
      const newReducers = {
        ...reducers,
        storeId: () => instanceId,
      };

      store = configureStore({
        reducer: createRootReducer(newReducers),
      });

      store.loadedReducers = newReducers;
      STORE_LIB[instanceId] = store;
      LOADED_WIDGETS[uniqueWidgetId] = true;
    }

    const handleError = useCallback((error) => {
      event('JAVASCRIPT_ERROR', {
        message: error.message,
        stack: error.stack,
      });
    }, []);

    return (
      <Provider store={store}>
        <PropsContext.Provider value={getWidgetProps(component, props)}>
          <div style={widgetStyle}>
            <ErrorBoundary name={component.id} onError={handleError}>
              {React.createElement(component, props)}
            </ErrorBoundary>
          </div>
        </PropsContext.Provider>
      </Provider>
    );
  };
};
